import { reachGoal } from 'application/utils/analytics'
import { CatalogFilters } from '../index/catalog/_filters'
import type { CatalogFiltersData } from '../types'

interface QuestSlotsResponse {
  quest_id: number
  price_label?: string
  slots_html?: string
}

// Каждая карточка запрашивает свои слоты отдельным запросом и показывает их
// сразу же, как только ответит именно её партнёр — карточки не ждут друг
// друга и не появляются все разом после самого медленного из них. Чтобы не
// запускать эти запросы сразу для всех квестов города, карточки открываются
// страницами (см. data-page-size / «Показать ещё» в initQuestSchedule), и
// сюда попадают только уже открытые карточки.
async function loadCardSlots(
  card: HTMLElement,
  dateParam: string
): Promise<void> {
  const questId = card.dataset.questId
  if (!questId || card.dataset.slotsLoaded || card.dataset.slotsLoading) {
    return
  }
  card.dataset.slotsLoading = 'true'

  const url = new URL('/api/schedule/slots', window.location.origin)
  url.searchParams.set('quest_id', questId)
  if (dateParam) {
    url.searchParams.set('date', dateParam)
  }

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const quest = (await response.json()) as QuestSlotsResponse
    card.dataset.slotsLoaded = 'true'
    delete card.dataset.slotsLoading

    if (!quest.slots_html) {
      card.classList.add('has-no-slots')
      return
    }

    const priceInfo = card.querySelector<HTMLElement>('[data-quest-price-info]')
    const price = card.querySelector<HTMLElement>('[data-quest-price]')
    if (price) {
      // format_price возвращает HTML (₽ обёрнут в <span class="currency-sign">),
      // поэтому innerHTML, а не textContent — иначе разметка уходит как текст.
      price.innerHTML = quest.price_label ?? ''
    }
    priceInfo?.classList.remove('is-hidden')

    const slots = card.querySelector<HTMLElement>('[data-quest-slots]')
    if (slots) {
      slots.innerHTML = quest.slots_html
    }
  } catch {
    // Ошибку запроса не считаем «слотов нет» — иначе сбой сети навсегда
    // прячет квест из расписания. slotsLoading снимается, а slotsLoaded не
    // проставляется, поэтому карточка попробует загрузиться заново при
    // следующем вызове loadVisibleSlots (пагинация, смена фильтра/даты).
    delete card.dataset.slotsLoading
  }
}

// Та же логика фильтрации, что и в filterCatalogQuests
// (application/quests/index/catalog/_filter-quests.ts), но по data-атрибутам
// уже отрисованной карточки, а не по JSON-объекту квеста — карточки
// расписания рендерятся сервером, а не пересобираются в JS.
export function cardMatchesFilters(
  card: HTMLElement,
  filters: CatalogFiltersData
): boolean {
  if (filters.type && card.dataset.questTypeId !== filters.type) {
    return false
  }

  if (filters.categories.length > 0) {
    const cardCategories = (card.dataset.categories || '')
      .split(' ')
      .filter(Boolean)
    const selected = new Set(
      filters.categories.map((category) => category.toLowerCase())
    )
    if (!cardCategories.some((slug) => selected.has(slug.toLowerCase()))) {
      return false
    }
  }

  if (
    filters.horror.length > 0 &&
    !filters.horror.includes(card.dataset.scare || '')
  ) {
    return false
  }

  if (
    filters.complexity.length > 0 &&
    !filters.complexity.includes(card.dataset.complexity || '')
  ) {
    return false
  }

  if (filters.metro && filters.metro.length > 0) {
    if (!card.dataset.metro || !filters.metro.includes(card.dataset.metro)) {
      return false
    }
  }

  const maxAge = filters.age ? Number(filters.age) : NaN
  if (!isNaN(maxAge)) {
    const cardAge = card.dataset.age ? parseInt(card.dataset.age, 10) : NaN
    if (!(isNaN(cardAge) || cardAge <= maxAge)) {
      return false
    }
  }

  if (filters.haveEnglishVersion && card.dataset.hasEnglishVersion !== 'true') {
    return false
  }

  if (filters.playersCount && typeof filters.playersCount === 'number') {
    const maxGamers = card.dataset.maxGamers
      ? Number(card.dataset.maxGamers)
      : NaN
    if (!(maxGamers >= filters.playersCount)) {
      return false
    }
  }

  return true
}

export function initQuestSchedule(): void {
  const schedule = document.querySelector<HTMLElement>('[data-quests-schedule]')

  if (!schedule) {
    return
  }

  const container = schedule.querySelector<HTMLElement>(
    '[data-schedule-quests]'
  )
  const noSlots = schedule.querySelector<HTMLElement>('[data-no-slots]')
  const loadMoreButton = schedule.querySelector<HTMLElement>('[data-load-more]')
  if (!container) {
    return
  }

  const cards = Array.from(
    container.querySelectorAll<HTMLElement>('[data-quest]')
  )
  const dateParam = schedule.dataset.selectedDate || ''
  const pageSize = Number(schedule.dataset.pageSize) || cards.length

  let revealedCount = Math.min(pageSize, cards.length)
  let filters: CatalogFiltersData = {
    type: '',
    categories: [],
    complexity: [],
    horror: []
  }

  // Пагинация показывает только первую страницу, пока фильтр не выбран —
  // это ограничивает число запросов слотов при первой загрузке. Но раз
  // фильтр включён, он обязан искать по всему городу, а не только по уже
  // раскрытой странице — иначе, скажем, метро, которого нет среди первых
  // 12 карточек, честно покажет «ничего не найдено» вместо реального совпадения.
  const isFilterActive = (value: CatalogFiltersData): boolean =>
    !!value.type ||
    value.categories.length > 0 ||
    value.horror.length > 0 ||
    value.complexity.length > 0 ||
    !!(value.metro && value.metro.length > 0) ||
    !!value.age ||
    !!value.haveEnglishVersion ||
    (typeof value.playersCount === 'number' && value.playersCount > 0)

  // Единственное место, решающее, видна ли карточка: страница показа (если
  // фильтр не активен), фильтры каталога и то, нашлись ли у неё слоты —
  // независимые условия, которые иначе легко рассинхронизировать, управляя
  // классом is-hidden из нескольких мест сразу.
  const refreshVisibility = (): void => {
    const filterActive = isFilterActive(filters)

    cards.forEach((card, index) => {
      const withinPage = filterActive || index < revealedCount
      const passesFilters = cardMatchesFilters(card, filters)
      const hasSlots = !card.classList.contains('has-no-slots')

      card.classList.toggle(
        'is-hidden',
        !(withinPage && passesFilters && hasSlots)
      )
    })
  }

  const visibleTargets = (): HTMLElement[] => {
    const filterActive = isFilterActive(filters)
    return filterActive
      ? cards.filter((card) => cardMatchesFilters(card, filters))
      : cards.slice(0, revealedCount)
  }

  const updateNoSlotsMessage = (): void => {
    if (!noSlots) {
      return
    }

    const filterActive = isFilterActive(filters)
    const targets = visibleTargets()
    // Пока не опрошены все карточки, которые должны быть видны (страница
    // или, при активном фильтре, все совпадения по городу), рано
    // утверждать, что слотов вообще нет — они могут найтись дальше.
    const allTargetsLoaded = targets.every((card) => card.dataset.slotsLoaded)
    const anyVisible = cards.some(
      (card) => !card.classList.contains('is-hidden')
    )

    noSlots.classList.toggle(
      'is-hidden',
      anyVisible ||
        !allTargetsLoaded ||
        (!filterActive && revealedCount < cards.length)
    )
  }

  const updateLoadMoreVisibility = (): void => {
    const filterActive = isFilterActive(filters)
    loadMoreButton?.classList.toggle(
      'is-hidden',
      filterActive || revealedCount >= cards.length
    )
  }

  const loadVisibleSlots = async (): Promise<void> => {
    await Promise.all(
      visibleTargets().map((card) => loadCardSlots(card, dateParam))
    )
    refreshVisibility()
    updateNoSlotsMessage()
  }

  refreshVisibility()
  updateLoadMoreVisibility()
  void loadVisibleSlots()

  loadMoreButton?.addEventListener('click', () => {
    revealedCount = Math.min(revealedCount + pageSize, cards.length)
    refreshVisibility()
    updateLoadMoreVisibility()
    void loadVisibleSlots()
  })

  const filtersElement = schedule.querySelector<HTMLElement>(
    '[data-catalog-filters]'
  )

  if (filtersElement) {
    new CatalogFilters({
      element: filtersElement,
      categories: [],
      typeOptions: [],
      onChange: (changedFilters) => {
        filters = changedFilters
        refreshVisibility()
        updateNoSlotsMessage()
        updateLoadMoreVisibility()
        void loadVisibleSlots()
      }
    })
  }

  const dateOffsetByValue: Record<string, number> = {
    'today': 0,
    'tomorrow': 1,
    'day-after-tomorrow': 2
  }

  const parseToday = (value: string | undefined): Date => {
    const [year, month, day] = (value ?? '').split('-').map(Number)

    if (!year || !month || !day) {
      return new Date()
    }

    return new Date(year, month - 1, day)
  }

  const pad = (value: number): string => String(value).padStart(2, '0')

  const applyDateFilter = (event: CustomEvent): void => {
    const { value } = event.detail
    const offset = dateOffsetByValue[value]

    if (typeof offset !== 'number') {
      return
    }

    reachGoal('new_FilterUse')

    // «Завтра» и «послезавтра» отсчитываются от сегодняшнего дня, а не от
    // выбранного, поэтому базой служит дата, проставленная сервером.
    const date = parseToday(schedule.dataset.today)
    date.setDate(date.getDate() + offset)

    const isoDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

    const url = new URL(window.location.href)
    url.searchParams.set('date', isoDate)
    // Выбор дня через календарь оставляет start_date; без удаления адрес
    // хранил бы две разные даты сразу.
    url.searchParams.delete('start_date')
    url.searchParams.delete('end_date')
    window.location.href = url.toString()
  }

  const dateDropdown = schedule.querySelector<HTMLElement>(
    '[data-dropdown-name="date"]'
  )

  dateDropdown?.addEventListener('dropdown-change', (event) =>
    applyDateFilter(event as CustomEvent)
  )
}
